/**
 * Giai đoạn 2 – Chánh án: Quyết định thẩm quyền (Thụ lý/Trả đơn/Chuyển đơn)
 * Danh sách đơn đã nộp (filed), AI phân tích thẩm quyền, nút quyết định.
 */
import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material'
import { Gavel, CheckCircle, Cancel, SwapHoriz, AutoAwesome } from '@mui/icons-material'
import { useGetCasesQuery, useUpdateCaseStatusMutation } from '../../store/api/casesApi'
import type { Case } from '../../types/case'

const CATEGORY_LABELS: Record<string, string> = {
  marriage_family: 'Hôn nhân & Gia đình',
  civil: 'Dân sự',
  land: 'Đất đai',
}

function formatClaimValue(vnd: number): string {
  if (vnd >= 1e9) return `${(vnd / 1e9).toFixed(1)} tỷ`
  return `${(vnd / 1e6).toFixed(0)} triệu`
}

function getCategoryLabel(category: Case['category']): string {
  return CATEGORY_LABELS[category] ?? (category === 'civil' ? 'Dân sự' : category)
}

/** Mock AI phân tích thẩm quyền – triển khai thật gọi API */
function getAIAnalysis(c: Case): { conclusion: string; suggestion: 'accept' | 'return' | 'transfer'; reason: string } {
  if (c.category === 'marriage_family' && c.claim_value_vnd < 500_000_000)
    return { conclusion: 'Đúng thẩm quyền', suggestion: 'accept', reason: 'Vụ Hôn nhân gia đình, giá trị trong phạm vi. Khuyến nghị thụ lý.' }
  if (c.claim_value_vnd > 1_000_000_000)
    return { conclusion: 'Cần xem xét thẩm quyền', suggestion: 'transfer', reason: 'Giá trị tranh chấp lớn, có thể thuộc TAND cấp trên. Cân nhắc chuyển đơn.' }
  return { conclusion: 'Đúng thẩm quyền', suggestion: 'accept', reason: 'Phân loại và thẩm quyền phù hợp. Khuyến nghị thụ lý.' }
}

export default function PreliminaryChiefPage({ embedded = false }: { embedded?: boolean }) {
  const { data: cases = [], refetch } = useGetCasesQuery()
  const [updateCaseStatus, { isLoading }] = useUpdateCaseStatusMutation()
  const [decisionModal, setDecisionModal] = useState<{ case: Case; action: 'return' | 'transfer' } | null>(null)
  const [returnReason, setReturnReason] = useState('Thiếu tài liệu đính kèm')
  const [transferNote, setTransferNote] = useState('Chuyển TAND quận 2 theo thẩm quyền lãnh thổ.')
  const [toast, setToast] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

  const filedCases = cases.filter((c) => c.status === 'filed')

  const handleAccept = async (c: Case) => {
    try {
      await updateCaseStatus({ case_id: c.id, status: 'accepted' }).unwrap()
      setToast({ open: true, message: `Đã quyết định thụ lý vụ ${c.case_code}. Thư ký sẽ gửi Giấy báo nộp tạm ứng án phí.` })
      refetch()
    } catch {
      setToast({ open: true, message: 'Có lỗi khi cập nhật.' })
    }
  }

  const handleReturn = () => {
    if (!decisionModal || decisionModal.action !== 'return' || !returnReason) return
    updateCaseStatus({ case_id: decisionModal.case.id, status: 'returned' }).unwrap()
      .then(() => {
        setToast({ open: true, message: 'Đã quyết định trả đơn. Thông báo sẽ gửi đến đương sự.' })
        setDecisionModal(null)
        setReturnReason('')
        refetch()
      })
      .catch(() => setToast({ open: true, message: 'Có lỗi khi cập nhật.' }))
  }

  const handleTransfer = () => {
    if (!decisionModal || decisionModal.action !== 'transfer') return
    updateCaseStatus({ case_id: decisionModal.case.id, status: 'transferred' }).unwrap()
      .then(() => {
        setToast({ open: true, message: 'Đã quyết định chuyển đơn. Hồ sơ sẽ chuyển theo quy định.' })
        setDecisionModal(null)
        setTransferNote('')
        refetch()
      })
      .catch(() => setToast({ open: true, message: 'Có lỗi khi cập nhật.' }))
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      {!embedded && (
        <>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Quyết định thẩm quyền (Giai đoạn 2)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Xử lý nội dung đơn, xác định thẩm quyền. AI hỗ trợ phân tích và gợi ý hướng xử lý. Chánh án ra quyết định: Thụ lý/Trả đơn/Chuyển đơn.
          </Typography>
        </>
      )}

      {filedCases.length === 0 ? (
        <Alert severity="info">Hiện không có đơn nào chờ quyết định thẩm quyền.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Mã vụ</strong></TableCell>
                <TableCell><strong>Loại</strong></TableCell>
                <TableCell align="right"><strong>Giá trị</strong></TableCell>
                <TableCell>Nguyên đơn/Bị đơn</TableCell>
                <TableCell>Ngày nộp</TableCell>
                <TableCell>AI phân tích</TableCell>
                <TableCell align="right">Quyết định</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filedCases.map((c) => {
                const ai = getAIAnalysis(c)
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>{c.case_code}</TableCell>
                    <TableCell>
                      <Chip label={getCategoryLabel(c.category)} size="small" />
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatClaimValue(c.claim_value_vnd)}
                    </TableCell>
                    <TableCell>{c.plaintiff_name ?? c.plaintiff_id} – {c.defendant_name ?? c.defendant_id}</TableCell>
                    <TableCell>{c.filed_at ? new Date(c.filed_at).toLocaleDateString('vi-VN') : '–'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                        <AutoAwesome fontSize="small" color="primary" />
                        <Typography variant="body2">{ai.conclusion}</Typography>
                        <Chip label={ai.suggestion === 'accept' ? 'Thụ lý' : ai.suggestion === 'return' ? 'Trả đơn' : 'Chuyển'} size="small" color={ai.suggestion === 'accept' ? 'success' : ai.suggestion === 'return' ? 'error' : 'warning'} variant="outlined" />
                      </Box>
                      <Typography variant="caption" color="text.secondary">{ai.reason}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="success" startIcon={<CheckCircle />} onClick={() => handleAccept(c)} disabled={isLoading}>
                        Thụ lý
                      </Button>
                      <Button size="small" color="error" startIcon={<Cancel />} onClick={() => setDecisionModal({ case: c, action: 'return' })}>
                        Trả đơn
                      </Button>
                      <Button size="small" color="warning" startIcon={<SwapHoriz />} onClick={() => setDecisionModal({ case: c, action: 'transfer' })}>
                        Chuyển đơn
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={!!decisionModal} onClose={() => setDecisionModal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decisionModal?.action === 'return' ? 'Trả đơn' : 'Chuyển đơn'} – {decisionModal?.case.case_code}
        </DialogTitle>
        <DialogContent>
          {decisionModal?.action === 'return' && (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Lý do trả đơn</InputLabel>
              <Select value={returnReason} label="Lý do trả đơn" onChange={(e) => setReturnReason(e.target.value)}>
                <MenuItem value="Thiếu chữ ký">Thiếu chữ ký</MenuItem>
                <MenuItem value="Thiếu tài liệu đính kèm">Thiếu tài liệu đính kèm</MenuItem>
                <MenuItem value="Nội dung không rõ ràng">Nội dung không rõ ràng</MenuItem>
                <MenuItem value="Không đúng thẩm quyền">Không đúng thẩm quyền</MenuItem>
                <MenuItem value="Khác">Khác</MenuItem>
              </Select>
            </FormControl>
          )}
          {decisionModal?.action === 'transfer' && (
            <TextField fullWidth label="Ghi chú chuyển đơn (Tòa án nhận)" value={transferNote} onChange={(e) => setTransferNote(e.target.value)} placeholder="Ví dụ: Chuyển TAND quận X theo thẩm quyền" sx={{ mt: 1 }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionModal(null)}>Hủy</Button>
          {decisionModal?.action === 'return' && (
            <Button variant="contained" color="error" onClick={handleReturn} disabled={!returnReason}>Xác nhận trả đơn</Button>
          )}
          {decisionModal?.action === 'transfer' && (
            <Button variant="contained" color="warning" onClick={handleTransfer}>Xác nhận chuyển đơn</Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={() => setToast((t) => ({ ...t, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setToast((t) => ({ ...t, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  )
}
